import React, { useEffect } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { EditorialIntro } from '../innovher/SplitSection';
import ApplyCta from '../innovher/ApplyCta';
import Magnetic from '../innovher/Magnetic';
import SEO from '../SEO';
import { STOCK } from '../../lib/images';

/** External InnovIdea site */
export const INNOVIDEA_SITE = 'https://innovidea.in';

const FOCUS_AREAS = [
  'Positioning',
  'Identity',
  'Storytelling',
  'Digital presence',
  'Campaigns',
  'Market communication',
];

function SectionEyebrow({ children }) {
  return (
    <div className="text-[#ff8ec5] text-xs tracking-[0.3em] uppercase mb-4">{children}</div>
  );
}

function VisitInnovIdea({ size = 'md', className = '' }) {
  const sizes = {
    sm: 'pl-5 pr-1.5 py-1.5 text-sm',
    md: 'pl-7 pr-2 py-2.5 text-base',
    lg: 'pl-8 pr-2.5 py-3 text-base sm:text-lg',
  };
  const icon = size === 'sm' ? 'w-7 h-7' : size === 'lg' ? 'w-10 h-10' : 'w-9 h-9';

  return (
    <Magnetic>
      <a
        href={INNOVIDEA_SITE}
        target="_blank"
        rel="noopener noreferrer"
        data-cursor="hover"
        className={`group inline-flex items-center gap-2 rounded-full bg-[#d42b92] text-white font-medium shadow-[0_15px_50px_-10px_rgba(212,43,146,0.55)] hover:bg-white hover:text-black transition-colors duration-300 ${sizes[size]} ${className}`}
      >
        Visit InnovIdea
        <span
          className={`${icon} rounded-full bg-white text-[#d42b92] grid place-items-center group-hover:bg-[#d42b92] group-hover:text-white group-hover:rotate-45 transition`}
        >
          <ArrowUpRight className="w-4 h-4" />
        </span>
      </a>
    </Magnetic>
  );
}

export default function InnovIdeaPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const els = document.querySelectorAll('.reveal-up');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-revealed');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <>
      <SEO
        title="InnovIdea | Ideas Deserve an Identity Powerful Enough to Travel"
        description="InnovIdea is InnovHer's brand, communication and growth practice, turning business ideas into brands that can be understood, experienced and remembered."
        keywords="InnovIdea, brand strategy, positioning, storytelling, digital presence, innovidea.in, InnovHer"
      />

      {/* Header / Purpose Section */}
      <section className="relative bg-[#08080a] pt-28 sm:pt-32 lg:pt-36 pb-16 sm:pb-20 lg:pb-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <EditorialIntro
            className="reveal-up"
            eyebrow="InnovIdea"
            logo="/new_images/logos/innovidea.webp"
            title={
              <>
                Ideas deserve an identity{' '}
                powerful enough to <span className="text-[#d42b92]">travel</span>.
              </>
            }
            lead="Brand, communication and growth practice, transforming business ideas into brands that can be understood, experienced and remembered."
          >
            <p>
              InnovIdea builds brand, communication and growth practice, transforming business
              ideas into brands that can be understood, experienced and remembered.
            </p>
            <p>
              From positioning and identity to storytelling, digital presence, campaigns and market
              communication, InnovIdea works at the intersection of business strategy and creative
              expression.
            </p>
          </EditorialIntro>
        </div>
      </section>

      <section className="relative bg-[#08080a] py-16 lg:py-24 border-t border-white/5 overflow-hidden">
        <div className="absolute top-1/2 right-0 w-[420px] h-[420px] rounded-full bg-[#d42b92]/10 blur-[140px] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left: Text content */}
            <div className="reveal-up">
              <SectionEyebrow>What we build</SectionEyebrow>
              <h2 className="font-semibold tracking-tight text-white text-3xl sm:text-4xl md:text-5xl leading-[1.08]">
                Beyond looking better -{' '}
                <span className="text-[#d42b92]">clarity that travels</span>
              </h2>
              <p className="mt-6 text-white/60 text-base sm:text-lg leading-relaxed">
                Its role extends beyond making ventures look better. It helps clarify what a business
                stands for, who it matters to and how that proposition can translate consistently
                across markets and touchpoints.
              </p>
              <p className="mt-4 text-white font-semibold text-lg">
                Because building a venture is also about building perception, trust and relevance -{' '}
                <span className="text-[#ff8ec5] italic">
                  turning an idea into an identity people can believe in.
                </span>
              </p>

              <div className="mt-8 flex flex-wrap gap-2.5">
                {FOCUS_AREAS.map((label) => (
                  <span
                    key={label}
                    className="px-4 py-2.5 rounded-full text-sm border border-white/10 bg-white/[0.03] text-white/75"
                  >
                    {label}
                  </span>
                ))}
              </div>
            </div>

            {/* Right: Image */}
            <div className="reveal-up relative">
              <div className="relative rounded-2xl lg:rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-black/60">
                <img
                  src="/new_images/innovidea.webp"
                  alt="InnovIdea brand, communication and growth"
                  className="w-full h-full object-cover aspect-[4/3]"
                  loading="lazy"
                />
                <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#d42b92]/20 to-transparent pointer-events-none" />
              </div>
              <div className="absolute -inset-4 -z-10 rounded-3xl bg-[#d42b92]/8 blur-2xl pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      <section className="relative bg-[#08080a] pt-4 pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="reveal-up rounded-[2rem] border border-[#d42b92]/30 bg-gradient-to-br from-[#d42b92]/20 via-black to-black p-8 md:p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/3 w-[420px] h-[420px] bg-[#d42b92]/20 rounded-full blur-[120px] pointer-events-none" />
            <div className="relative z-10 max-w-2xl">
              <SectionEyebrow>InnovIdea</SectionEyebrow>
              <h2 className="text-3xl md:text-4xl font-semibold text-white leading-tight">
                Explore the InnovIdea website
              </h2>
              <p className="mt-4 text-white/65 text-lg leading-relaxed">
                Visit innovidea.in for the full brand, communication and growth practice, or start
                a conversation with InnovHer to build together.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <VisitInnovIdea size="lg" />
                <ApplyCta
                  to="/contact"
                  label="Contact Us"
                  size="md"
                  variant="secondary"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
